import { Brand } from "effect"
import { href } from "react-router"

export type Route = Brand.Branded<string, "Global/Route">
const Route = Brand.nominal<Route>()

export type GlobalRoutes = typeof GlobalRoutes

export const GlobalRoutes = {
  // auth
  login: ({ returnTo }: { returnTo?: string } = {}) =>
    returnTo ? Route(`${href("/auth/login")}?returnTo=${returnTo}`) : Route(href("/auth/login")),
  verify: Route(href("/auth/verify")),
  resendOtp: Route(href("/auth/resend-otp")),
  onboarding: Route(href("/auth/onboarding")),
  logout: Route(href("/auth/logout")),
  oAuthCallback: Route(href("/auth/callback")),

  // app
  profile: Route(href("/profile")),

  join: {
    landing: Route(href("/join")),
    apiClaim: Route(href("/join/api/claim")),
    confirmation: Route(href("/join/confirmation"))
  },

  playlist: {
    download: ({ playlistId, type }: { playlistId: string; type: "MERCH_ITEM" | "PROFILE" }) =>
      Route(href(`/playlists/:playlistId/download`, { playlistId }) + `?type=${type}`),
    requestDownload: ({ playlistId, type }: { playlistId: string; type: "MERCH_ITEM" | "PROFILE" }) =>
      Route(href(`/playlists/:playlistId/download-request`, { playlistId }) + `?type=${type}`),
    downloadWithToken: Route(href("/playlists/download"))
  },

  checkIn: {
    landing: Route(href("/check-in")),
    phoneNumber: Route(href("/check-in/phone-number")),
    verifyLocation: Route(href("/check-in/verify-location")),
    createRequest: Route(href("/check-in/create-request")),
    confirmation: Route(href("/check-in/confirmation"))
  },

  admin: {
    // Admin auth
    login: Route(href("/admin/login")),
    loginWithMfa: Route(href("/admin/login/mfa")),
    verifyOtp: Route(href("/admin/verify-otp")),
    oAuthCallback: Route(href("/admin/oauth-callback")),
    setupMfa: Route(href("/admin/setup-mfa")),
    logout: Route(href("/admin/logout")),

    digitalProofs: Route(href("/admin/digital-proofs")),
    digitalProofsDetail: (digitalProofId: string) =>
      Route(href(`/admin/digital-proofs/:digitalProofId`, { digitalProofId })),
    digitalProofsEdit: (digitalProofId: string) =>
      Route(href(`/admin/digital-proofs/:digitalProofId/edit`, { digitalProofId })),
    digitalProofNew: Route(href("/admin/digital-proofs/new")),
    sweepstake: Route(href("/admin/sweepstake")),
    sweepstakeConversation: (userId: string) => Route(`/admin/sweepstake/conversation/${userId}`),
    sweepstakeMessageAll: (sweepstakeId: string) => Route(`/admin/sweepstakes/${sweepstakeId}/message-all`),
    sweepstakeSelectWinners: (sweepstakeId: string) => Route(`/admin/sweepstakes/${sweepstakeId}/winners`),
    shows: Route(href("/admin/shows")),
    showsDetail: (showId: string) => Route(`/admin/shows/${showId}`),
    showsEdit: (showId: string) => Route(`/admin/shows/${showId}/edit`),
    showsCheckIn: (showId: string) => Route(`/admin/shows/${showId}/check-in`),
    showsExclusivesAdd: (showId: string) => Route(`/admin/shows/${showId}/exclusives/add`),
    showsNew: Route(`/admin/shows/new`),
    exclusives: Route(href("/admin/exclusives")),
    exclusivesDetail: (exclusiveId: string) => Route(`/admin/exclusives/${exclusiveId}`),
    exclusivesNew: Route(href("/admin/exclusives/new")),
    exclusivesLink: Route(href("/admin/exclusives/link")),
    insights: Route(href("/admin/insights")),
    insightsLive: Route(href("/admin/insights/live")),
    merchSkus: Route(href("/admin/merch-skus")),
    merchSkusDetail: (merchSkuId: string) => Route(href(`/admin/merch-skus/:merchSkuId`, { merchSkuId })),
    merchSkusEdit: (merchSkuId: string) => Route(href(`/admin/merch-skus/:merchSkuId/edit`, { merchSkuId })),
    merchSkusMint: (merchSkuId: string) => Route(href(`/admin/merch-skus/:merchSkuId/mint`, { merchSkuId })),
    merchItemAdminTap: (merchItemId: string) =>
      Route(href(`/admin/merch-items/:merchItemId/admin-tap`, { merchItemId })),
    merchSkusNew: Route(href("/admin/merch-skus/new")),
    fans: Route(href("/admin/fans")),
    fansDetail: (userId: string) => Route(href(`/admin/fans/:userId`, { userId })),
    home: Route(href("/admin"))
  },

  merch: {
    landing: Route(href("/merch")),
    claim: Route(href("/merch/claim")),
    tap: ({ iykTap }: { iykTap?: string } = {}) =>
      Route(`${href("/merch/tap")}${iykTap ? `?iykTap=${iykTap}` : ""}`),
    confirmation: Route(href("/merch/confirmation"))
  }
} as const
